import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Loader2 } from "lucide-react";
import { STORE_API, imgUrl, inr, useStoreBrands } from "../../store/CartContext";
import { BREEDS, CATEGORIES, shopLink } from "../../data/shopMenu";

const QUICK = [
  ["Grooming packages", "/grooming"], ["Dog food", shopLink("Dog", "food")], ["Cat litter", shopLink("Cat", "litter")],
];

export default function SearchBox({ className = "", autoFocus = false, onDone }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [res, setRes] = useState(null);
  const navigate = useNavigate();
  const liveBrands = useStoreBrands() || [];
  const ref = useRef(null);

  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setRes(null); return; }
    const h = setTimeout(() => {
      axios.get(`${STORE_API}/products`, { params: { q: t, per_page: 5 } }).then((r) => setRes(r.data.items)).catch(() => setRes([]));
    }, 200);
    return () => clearTimeout(h);
  }, [q]);

  useEffect(() => {
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const go = (to) => { setOpen(false); setQ(""); onDone?.(); navigate(to); };
  const t = q.trim().toLowerCase();
  const brands = t.length > 1 ? liveBrands.filter((b) => b.toLowerCase().includes(t)).slice(0, 3) : [];
  const breeds = t.length > 1 ? BREEDS.filter((b) => b.name.toLowerCase().includes(t)).slice(0, 2) : [];
  const cats = t.length > 1 ? Object.entries(CATEGORIES).filter(([, l]) => l.toLowerCase().includes(t)).slice(0, 3) : [];
  const grooming = t.length > 1 && /groom|spa|bath|hair|massage|demat/.test(t);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <form onSubmit={(e) => { e.preventDefault(); if (q.trim()) go(`/shop/all?q=${encodeURIComponent(q.trim())}`); }}>
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#3915AA]/60" />
        <input value={q} autoFocus={autoFocus} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          placeholder="Search for dog food, cat litter, brands, or grooming packages..." data-testid="header-search-input"
          className="h-11 w-full rounded-full border-0 bg-white pl-11 pr-4 text-sm text-[#1E0A5C] placeholder-[#5E5675]/70 outline-none ring-2 ring-transparent focus:ring-[#FC5106]" />
      </form>
      {open && (
        <div data-testid="search-suggestions" className="absolute left-0 right-0 top-[calc(100%+8px)] z-[80] overflow-hidden rounded-2xl bg-white text-[#1E0A5C] shadow-2xl ring-1 ring-[#3915AA]/10">
          {t.length < 2 ? (
            <div className="p-4">
              <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#5E5675]">Popular</p>
              <div className="flex flex-wrap gap-2">{QUICK.map(([l, to]) => <button key={l} onClick={() => go(to)} className="rounded-full bg-[#F7F5FF] px-3 py-1.5 text-xs font-semibold hover:bg-[#3915AA]/10">{l}</button>)}</div>
            </div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto py-2">
              {grooming && <button onClick={() => go("/grooming")} data-testid="suggest-grooming" className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-[#F7F5FF]"><b>Grooming &amp; Spa packages</b></button>}
              {[...cats.map(([k, l]) => [l, shopLink(null, k), "Category"]), ...brands.map((b) => [b, `/shop/all?brand=${encodeURIComponent(b)}`, "Brand"]), ...breeds.map((b) => [b.name, `/breeds/${b.slug}`, "Breed"])].map(([l, to, kind]) => (
                <button key={kind + l} onClick={() => go(to)} className="flex w-full items-center justify-between px-4 py-2 text-left text-sm hover:bg-[#F7F5FF]"><span className="font-semibold">{l}</span><span className="text-[10px] uppercase tracking-wider text-[#5E5675]">{kind}</span></button>
              ))}
              {res === null ? <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-[#FC5106]" /></div> : res.map((p) => (
                <button key={p.id} onClick={() => go(`/shop/p/${p.slug}`)} data-testid={`suggest-product-${p.id}`} className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-[#F7F5FF]">
                  <img src={imgUrl(p.images?.[0])} alt="" className="h-10 w-10 rounded-lg object-cover" />
                  <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold">{p.name}</span><span className="text-xs text-[#5E5675]">{p.brand}</span></span>
                  <span className="text-sm font-bold">{inr(p.min_price)}</span>
                </button>
              ))}
              {res && <button onClick={() => go(`/shop/all?q=${encodeURIComponent(q.trim())}`)} className="w-full px-4 py-2.5 text-left text-sm font-bold text-[#FC5106] hover:bg-[#F7F5FF]">See all results for "{q.trim()}"</button>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
